import * as React from 'react';
import { Form } from 'antd';
import { WorksheetModel, NodeBaseModel, InputBaseModel } from '../models';
import { Utils } from '../../custom_shared/misc';
import { NodeFormValues } from '../types';
import { useAppStore } from '../stores';
import { Subscription } from 'rxjs';

export default (worksheet: WorksheetModel) => {
    const { formStore, inputsStore } = useAppStore();

    const [formKey, setFormKey] = React.useState(0);

    const [form] = Form.useForm<NodeFormValues>();

    const isInitialLoad = React.useRef(true);

    const onValuesChange = (changedValues: NodeFormValues) => {
        Object.keys(changedValues).forEach(inputGuid => handleInputValuesChange(inputGuid));

        worksheet.setHasChangedData(true);

        if (!worksheet.hasValidationError) {
            return;
        }

        setTimeout(() => {
            form.validateFields()
                .then(() => worksheet.setHasValidationError(false))
                .catch(() => {
                    // Do nothing this is just validation error
                });
        }, 50);
    };

    const setFormFieldValue = React.useCallback(
        (input: InputBaseModel) => {
            form.setFieldsValue({
                [input.guid]: input.dataType === 'DateTime' ? Utils.convertDateValueToDate(input.value) : input.value
            });
        },
        [form]
    );

    const handleInputValuesChange = (inputGuid: string) => {
        const input = worksheet.getInputByGuid(inputGuid);

        if (!input) {
            return;
        }

        const value = form.getFieldValue(inputGuid);
        inputsStore.handleInputModification(input, value);
    };

    const setNodeFormFieldValues = React.useCallback(
        (nodeToProcess: NodeBaseModel) => {
            nodeToProcess.inputs.forEach(setFormFieldValue);

            nodeToProcess.childNodes.forEach(childNode => {
                setNodeFormFieldValues(childNode);
            });
        },
        [setFormFieldValue]
    );

    const setWorksheetFormFieldValues = React.useCallback(
        (worksheetToProcess: WorksheetModel) => {
            worksheetToProcess.nodes.forEach(node => {
                setNodeFormFieldValues(node);
            });
        },
        [setNodeFormFieldValues]
    );

    const getNodeSubscriptions = React.useCallback(
        (nodeToProcess: NodeBaseModel) => {
            const subscriptions = [
                nodeToProcess.inputValueChangeSubject.subscribe(setFormFieldValue),
                nodeToProcess.inputValidationSubject.subscribe(input =>
                    form.validateFields([input.guid]).catch(() => {
                        // catch to avoid unhandled promise rejection error in console
                    })
                )
            ];

            nodeToProcess.childNodes.forEach(childNode => {
                subscriptions.push(...getNodeSubscriptions(childNode));
            });

            return subscriptions;
        },
        [setFormFieldValue, form]
    );

    const getWorksheetSubscriptions = React.useCallback(
        (worksheetToProcess: WorksheetModel) => {
            const subscriptions: Subscription[] = [];

            worksheetToProcess.nodes.forEach(node => {
                subscriptions.push(...getNodeSubscriptions(node));
            });

            return subscriptions;
        },
        [getNodeSubscriptions]
    );

    React.useEffect(() => {
        formStore.addFormRef(worksheet.id, form);
        return () => formStore.removeFormRef(worksheet.id);
    }, [worksheet, form, formStore]);

    React.useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        setFormKey(prevKey => prevKey + 1);
    }, [worksheet]);

    React.useEffect(() => {
        const subscriptions = getWorksheetSubscriptions(worksheet);
        return () => subscriptions.forEach(subscription => subscription.unsubscribe());
    }, [form, worksheet, getWorksheetSubscriptions]);

    React.useEffect(() => {
        setWorksheetFormFieldValues(worksheet);
    }, [worksheet, worksheet.nodes, setWorksheetFormFieldValues]);

    return {
        form,
        formKey,
        onValuesChange
    };
};
